(function () {
  'use strict';

  if (customElements.get('jmt-featured-product-break')) return;

  class JmtFeaturedProductBreak extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.waitingForDocument) return;

      if (document.readyState === 'loading' && !this.querySelector('[data-jmt-featured-product-json]')) {
        this.waitingForDocument = true;
        this.onDocumentReady = () => {
          this.waitingForDocument = false;
          if (this.isConnected) this.initialize();
        };
        document.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
        return;
      }

      this.initialize();
    }

    initialize() {
      if (this.initialized) return;
      this.initialized = true;

      this.product = this.readProduct();
      this.modal = document.getElementById(this.dataset.modalId);
      this.openButton = this.querySelector('[data-jmt-featured-open]');
      this.productLinks = Array.from(new Set([
        ...this.querySelectorAll('a[href]'),
        ...(this.modal ? this.modal.querySelectorAll('a[href]') : [])
      ]));
      this.modalPrice = this.modal && this.modal.querySelector('[data-jmt-featured-modal-price]');
      this.selects = this.modal ? Array.from(this.modal.querySelectorAll('[data-jmt-featured-option]')) : [];
      this.variantInput = this.modal && this.modal.querySelector('[data-jmt-featured-variant-id]');
      this.submitButton = this.modal && this.modal.querySelector('[data-jmt-featured-submit]');
      this.submitLabel = this.modal && this.modal.querySelector('[data-jmt-featured-submit-label]');
      this.productForm = this.modal && this.modal.querySelector('form[data-type="add-to-cart-form"]');

      this.onOpen = this.open.bind(this);
      this.onOptionChange = this.updateVariant.bind(this);
      this.onProductSubmit = this.markPendingAdd.bind(this);
      this.onProductLinkClick = this.trackProductView.bind(this);
      this.onPreferenceChange = this.applyPreference.bind(this);
      this.onSectionUnload = this.removeSectionModal.bind(this);
      this.onModalClosed = this.handleModalClosed.bind(this);

      if (this.openButton) this.openButton.addEventListener('click', this.onOpen);
      if (this.productForm) this.productForm.addEventListener('submit', this.onProductSubmit);
      this.productLinks.forEach((link) => link.addEventListener('click', this.onProductLinkClick));
      this.selects.forEach((select) => select.addEventListener('change', this.onOptionChange));
      window.addEventListener('jmt:pref:change', this.onPreferenceChange);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      document.body.addEventListener('modalClosed', this.onModalClosed);

      this.updateVariant();
      this.waitForPreferenceEngine();
      this.waitForCartSubscription();
    }

    disconnectedCallback() {
      if (this.onDocumentReady) document.removeEventListener('DOMContentLoaded', this.onDocumentReady);
      this.waitingForDocument = false;
      if (this.openButton) this.openButton.removeEventListener('click', this.onOpen);
      if (this.productForm) this.productForm.removeEventListener('submit', this.onProductSubmit);
      (this.productLinks || []).forEach((link) => link.removeEventListener('click', this.onProductLinkClick));
      (this.selects || []).forEach((select) => select.removeEventListener('change', this.onOptionChange));
      window.removeEventListener('jmt:pref:change', this.onPreferenceChange);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.body.removeEventListener('modalClosed', this.onModalClosed);
      window.clearTimeout(this.preferenceTimer);
      window.clearTimeout(this.subscriptionTimer);
      window.clearTimeout(this.pendingAddTimer);
      if (this.unsubscribeCart) this.unsubscribeCart();

      if (this.modal) {
        if (this.modal.hasAttribute('open') && typeof this.modal.hide === 'function') this.modal.hide();
        this.modal.remove();
      }
      this.unlockPageScroll();
      this.initialized = false;
    }

    readProduct() {
      const node = this.querySelector('[data-jmt-featured-product-json]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    open() {
      if (!this.modal || typeof this.modal.show !== 'function') return;
      this.loadModalImage();
      this.applyPreference();
      const scrollPosition = window.scrollY;
      this.modal.show(this.openButton);
      if (window.matchMedia('(max-width: 749px)').matches) this.lockPageScroll(scrollPosition);
      this.trackInteraction('modal_open');
    }

    loadModalImage() {
      const image = this.modal && this.modal.querySelector('[data-jmt-featured-modal-image]');
      if (!image || image.hasAttribute('src')) return;

      const srcset = image.getAttribute('data-srcset');
      const src = image.getAttribute('data-src');
      if (srcset) image.setAttribute('srcset', srcset);
      if (src) image.setAttribute('src', src);
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
    }

    lockPageScroll(scrollPosition) {
      if (this.pageScrollState) return;
      const body = document.body;
      const scrollY = Number.isFinite(scrollPosition) ? scrollPosition : window.scrollY;
      this.pageScrollState = {
        scrollY,
        position: body.style.position,
        top: body.style.top,
        right: body.style.right,
        left: body.style.left,
        width: body.style.width,
        overflow: body.style.overflow
      };
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.right = '0';
      body.style.left = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }

    unlockPageScroll() {
      if (!this.pageScrollState) return;
      const body = document.body;
      const state = this.pageScrollState;
      this.pageScrollState = null;
      body.style.position = state.position;
      body.style.top = state.top;
      body.style.right = state.right;
      body.style.left = state.left;
      body.style.width = state.width;
      body.style.overflow = state.overflow;
      window.scrollTo(0, state.scrollY);
    }

    handleModalClosed() {
      if (!this.modal || !this.modal.hasAttribute('open')) return;
      this.unlockPageScroll();
    }

    waitForPreferenceEngine(attempt) {
      const tries = Number(attempt || 0);
      if (window.JMT_PREF && window.JMT_VARIANTS) {
        this.applyPreference();
        return;
      }
      if (tries >= 30) return;
      this.preferenceTimer = window.setTimeout(() => this.waitForPreferenceEngine(tries + 1), 100);
    }

    applyPreference() {
      if (!window.JMT_PREF || !window.JMT_VARIANTS || !this.selects.length) return;
      const preference = window.JMT_PREF.get();
      if (!preference) return;

      this.selects.forEach((select) => {
        const optionName = select.dataset.optionName || select.name.replace(/^options\[|\]$/g, '');
        const wanted = window.JMT_VARIANTS.wantedValue(preference, optionName);
        if (!wanted) return;
        const match = Array.from(select.options).find((option) => {
          return window.JMT_VARIANTS.normalizeValue(optionName, option.value) ===
            window.JMT_VARIANTS.normalizeValue(optionName, wanted);
        });
        if (match) select.value = match.value;
      });

      this.updateVariant();
    }

    selections() {
      return this.selects.reduce((values, select) => {
        const optionName = select.dataset.optionName || select.name.replace(/^options\[|\]$/g, '');
        values[optionName] = select.value;
        return values;
      }, {});
    }

    findVariant() {
      if (!this.product) return null;
      if (!this.selects.length) return this.product.variants && this.product.variants[0];

      if (window.JMT_VARIANTS) {
        return window.JMT_VARIANTS.findVariant(this.product, this.selections(), { requireAll: true });
      }

      const selected = this.selects.map((select) => String(select.value));
      return (this.product.variants || []).find((variant) => {
        return variant.options.every((value, index) => String(value) === selected[index]);
      }) || null;
    }

    updateVariant() {
      const variant = this.findVariant();
      this.updateOptionAvailability();

      if (this.variantInput) {
        this.variantInput.value = variant ? variant.id : '';
        this.variantInput.disabled = !variant || variant.available === false;
      }

      const label = variant ? this.formatMoney(variant.price) : '';
      if (this.modalPrice && label) this.modalPrice.textContent = label;

      if (!this.submitButton || !this.submitLabel) return;
      const available = Boolean(variant && variant.available !== false);
      this.submitButton.disabled = !available;
      this.submitButton.toggleAttribute('aria-disabled', !available);
      this.submitLabel.textContent = available
        ? `${this.dataset.addLabel || 'Add to cart'} · ${this.formatMoney(variant.price, false)}`
        : (variant ? this.dataset.soldOutLabel : this.dataset.unavailableLabel) || 'Unavailable';
    }

    updateOptionAvailability() {
      if (!window.JMT_VARIANTS || !this.product || !this.selects.length) return;
      const selections = this.selections();
      this.selects.forEach((select) => {
        const optionName = select.dataset.optionName || select.name.replace(/^options\[|\]$/g, '');
        const availableValues = window.JMT_VARIANTS.availableValues(this.product, optionName, selections);
        Array.from(select.options).forEach((option) => {
          if (
            option.hasAttribute('data-jmt-guide-option') ||
            /^__JMT_(?:POUCH|STRAP)_GUIDE__$/.test(String(option.value || ''))
          ) {
            option.disabled = false;
            return;
          }
          option.disabled = !availableValues[window.JMT_VARIANTS.norm(option.value)];
        });
      });
    }

    formatMoney(cents, includeCurrency) {
      const amount = Number(cents || 0) / 100;
      const currency = (this.product && this.product.currency) || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency,
          currencyDisplay: includeCurrency === false ? 'symbol' : 'code'
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}${includeCurrency === false ? '' : ` ${currency}`}`;
      }
    }

    markPendingAdd() {
      if (!this.variantInput || !this.variantInput.value || !this.submitButton || this.submitButton.disabled) return;
      this.pendingVariantId = String(this.variantInput.value);
      window.clearTimeout(this.pendingAddTimer);
      this.pendingAddTimer = window.setTimeout(() => {
        this.pendingVariantId = '';
      }, 15000);
    }

    trackProductView() {
      this.trackInteraction('view_product');
    }

    trackInteraction(interaction) {
      const payload = {
        interaction: String(interaction || ''),
        product_id: this.dataset.productId || '',
        collection_handle: this.dataset.collectionHandle || '',
        collection_page: Number(this.dataset.collectionPage || 1)
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'jmt_collection_feature',
        ...payload
      });

      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('jmt:collection_feature', payload);
        }
      } catch (error) {}
    }

    waitForCartSubscription(attempt) {
      const tries = Number(attempt || 0);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source !== 'product-form') return;
          if (!this.pendingVariantId) return;
          if (String(event.productVariantId || '') !== this.pendingVariantId) return;
          this.pendingVariantId = '';
          window.clearTimeout(this.pendingAddTimer);
          this.trackInteraction('add_to_cart');
          if (this.modal && this.modal.hasAttribute('open')) this.modal.hide();
        });
        return;
      }
      if (tries >= 30) return;
      this.subscriptionTimer = window.setTimeout(() => this.waitForCartSubscription(tries + 1), 100);
    }

    removeSectionModal(event) {
      if (!event || !event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      if (!this.modal) return;
      if (this.modal.hasAttribute('open') && typeof this.modal.hide === 'function') this.modal.hide();
      else this.unlockPageScroll();
      this.modal.remove();
    }
  }

  customElements.define('jmt-featured-product-break', JmtFeaturedProductBreak);
})();
